import { LoggerService } from '../logger/logger.service';
import { LocalStorageManagerService } from '../storage/local-storage-manager.service';

/** Used as the AES key when no server-provided app ID is stored. */
const DEFAULT_JIO_CLOUD_APP_ID = '0123456789abcdef0123456789abcdef';

/**
 * Customer secret for HyLite credential encryption (`jioCloudAppId` from app config,
 * same value `decryptJioCloudData` below uses as its AES key). Mirrors Consumer's
 * core/config/crypto.ts `getCustomerSecret()` — kept as a thin wrapper over the existing
 * `getAppId` DI pattern already used by ApiKeyCacheService rather than a module-level
 * LocalStorageManager singleton (Consumer's shape), since this file has no direct
 * storage access of its own here.
 */
export function getCustomerSecret(localStorageManager: LocalStorageManagerService): string {
  return localStorageManager.getAppId(DEFAULT_JIO_CLOUD_APP_ID);
}

/**
 * Decrypts BuildConfig API keys using the Web Crypto API (AES-256-GCM),
 * aligned with the server's encrypted key format. Direct port of Consumer's
 * core/config/crypto.ts — kept as free functions (not a service) since it's
 * pure crypto with no Angular DI needs beyond the logger, which callers pass in.
 *
 * Format: `encryptedBase64,ivBase64`. Plaintext values (no comma) pass through unchanged.
 */
export async function decryptJioCloudData(encryptedCombo: string, logger: LoggerService, appId: string): Promise<string> {
  const commaIdx = encryptedCombo.indexOf(',');
  if (commaIdx === -1) {
    return encryptedCombo;
  }

  const encryptedData = encryptedCombo.slice(0, commaIdx);
  const ivBase64 = encryptedCombo.slice(commaIdx + 1);

  try {
    const keyBytes = new TextEncoder().encode(appId || DEFAULT_JIO_CLOUD_APP_ID);

    const cryptoKey = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, ['decrypt']);

    const ivBytes = Uint8Array.from(atob(ivBase64), (c) => c.charCodeAt(0));
    const cipherBytes = Uint8Array.from(atob(encryptedData), (c) => c.charCodeAt(0));

    const decryptedBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivBytes, tagLength: 128 }, cryptoKey, cipherBytes);

    return new TextDecoder().decode(decryptedBuffer);
  } catch (err) {
    logger.error('crypto', 'decryptJioCloudData failed — returning raw combo as fallback', err);
    return encryptedCombo;
  }
}
